<script lang="ts">
  import { currentRawFirehose, currentFirehoseStatus, selectedWorkoutUUID, saveStatus } from "../lib/state";
  import { workouts } from "../lib/workoutsStore";
  import {
    typeLabel,
    metadataLabel,
    decodeMetadataValue,
    EVENT_TYPE_BLURBS,
  } from "../lib/healthKitLabels";
  import { visibleFirehoseBuckets } from "../lib/firehoseView";
  import { BUILD_INFO, buildSourceLabel } from "../lib/buildInfo";
  import { nativeWebBuild } from "../lib/nativeMethods";
  import { _saveLog } from "../lib/liveCorrection";
  import { readJournal } from "../lib/labelJournal";
  import { readGoalJournal } from "../lib/goalJournal";
  import { get } from "svelte/store";
  import { showSaveTrouble } from "../lib/saveIndicator";
  import { buildDiagReport } from "../lib/saveDiagnostics";

  // Save-health readout (2026-07-09, Round 9 field diagnosis): the owner needs
  // to see — from his phone, no Mac — which web build is rendering and exactly
  // why any save was scored a failure. Deployable over the air; refreshed on
  // each expand so it reflects the moment you look.
  let showSaveHealth = $state(false);
  let saveHealth = $state({ log: [] as ReturnType<typeof _saveLog>, labelEntries: 0, goalEntries: 0, detail: "" });
  let copyState = $state<"idle" | "copied" | "failed">("idle");
  function refreshSaveHealth() {
    const labels = readJournal();
    const goal = readGoalJournal();
    saveHealth = {
      log: _saveLog().slice().reverse(),
      labelEntries: labels.length,
      goalEntries: goal ? 1 : 0,
      detail: JSON.stringify({ labels, goal }, null, 1).slice(0, 1200),
    };
  }
  // One-tap diagnostic export (owner request, 2026-07-09): the FULL picture —
  // build, save log, raw journals, timestamps — as pasteable JSON, so field
  // debugging doesn't depend on screenshots of a scrolling panel.
  async function copySaveHealth() {
    const payload = JSON.stringify(
      {
        webBuild: BUILD_INFO.commitSha,
        exportedAt: new Date().toISOString(),
        saveLog: _saveLog(),
        labelJournal: readJournal(),
        goalJournal: readGoalJournal(),
      },
      null,
      2,
    );
    try {
      await navigator.clipboard.writeText(payload);
      copyState = "copied";
    } catch {
      copyState = "failed";
    }
    setTimeout(() => (copyState = "idle"), 2500);
  }

  // "Send the report" (owner request, 2026-07-13): a real, obvious, one-tap
  // button — the owner could not even FIND the existing "Copy full report"
  // button, and has failed 3x to get a clipboard report back to us over
  // Slack while low-vision and at work. This POSTs the same redacted
  // diagnostic picture straight to the Worker's /api/diag route, which logs
  // it for `wrangler tail` — no clipboard, no paste, no dictating text.
  //
  // ABSOLUTE url, not relative: when the app has fallen back to its BUNDLED
  // copy the page origin is file:// / null, not https://swimset.club, and a
  // relative fetch would resolve against that null origin and silently fail
  // in exactly the case most worth reporting. text/plain keeps this a CORS
  // "simple" request from that null origin (an application/json content
  // type would force a preflight there); the Worker still returns
  // Access-Control-Allow-Origin on the response either way (see worker.ts).
  let sendState = $state<"idle" | "sending" | "sent" | "failed">("idle");
  async function sendReport() {
    if (sendState === "sending") return;
    sendState = "sending";
    const report = buildDiagReport({
      commitSha: BUILD_INFO.commitSha,
      // The shell's answer FIRST: a downloaded build and the compiled-in copy are both
      // file:// URLs, so the href alone cannot say which build is running.
      loadedFrom: `${buildSourceLabel(location.href, $nativeWebBuild)} · ${location.href}`,
      online: navigator.onLine,
      pillShowing: get(showSaveTrouble),
      labelJournalCount: readJournal().length,
      goalJournalCount: readGoalJournal() ? 1 : 0,
      saveStatus: get(saveStatus),
      swimUuid: dump?.workout.uuid,
      saveLog: _saveLog(),
    });
    try {
      const res = await fetch("https://swimset.club/api/diag", {
        method: "POST",
        headers: { "content-type": "text/plain;charset=UTF-8" },
        body: JSON.stringify(report),
      });
      sendState = res.ok ? "sent" : "failed";
    } catch {
      sendState = "failed";
    }
    setTimeout(() => (sendState = "idle"), 2500);
  }
  function sendReportLabel(s: typeof sendState): string {
    if (s === "sending") return "Sending…";
    if (s === "sent") return "Sent ✓";
    if (s === "failed") return "Couldn't send — no signal?";
    return "Send the report";
  }

  // HYG-05: the Firehose browser FOLLOWS the selected swim. It renders from the
  // shared `currentRawFirehose` store, which Prototypes (mounted alongside this
  // view on the Swim panel) populates after fetching `$selectedWorkoutUUID`.
  // Consuming the store — instead of doing our own most-recent fetch in onMount —
  // guarantees the raw browser and the prototype views always show the SAME swim
  // and avoids a duplicate bridge round-trip. `null` = cold-start / no swim yet.
  let dump = $derived($currentRawFirehose);
  // `$selectedWorkoutUUID` only picks the message wording on the loading path;
  // it does NOT guard against staleness. Staleness is prevented by Prototypes
  // clearing `currentRawFirehose` (and publishing `currentFirehoseStatus`) on
  // every empty / error transition — this view just mirrors that status.
  let expanded: Record<string, boolean> = $state({});
  let eventsExpanded = $state(false);
  let activitiesExpanded = $state(false);

  function toggleBucket(typeName: string) {
    expanded[typeName] = !expanded[typeName];
  }

  function fmtNum(n: number | null): string {
    if (n === null || n === undefined) return "—";
    if (Number.isInteger(n)) return n.toString();
    return n.toFixed(3);
  }

  function fmtDate(s: string): string {
    return new Date(s).toLocaleString();
  }

  function fmtTime(s: string): string {
    return new Date(s).toLocaleTimeString();
  }

  function fmtDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}m ${s.toString().padStart(2, "0")}s`;
  }

  function renderMetadata(md: Record<string, unknown> | null | undefined): { label: string; value: string }[] {
    if (!md) return [];
    return Object.entries(md).map(([k, v]) => ({
      label: metadataLabel(k),
      value: decodeMetadataValue(k, v),
    }));
  }
</script>

<!-- No swims (Health declined, or a new swimmer): no data point to show, so no
     section, no build line and no red read error under an empty card (Beta App
     Review walk, 2026-09-13). -->
{#if $workouts.length > 0}
<section class="firehose">
  <h2>Every data point, this swim</h2>
  <p class="subtitle">Every health reading Apple recorded during this swim.</p>

  <!-- Save-health readout (field diagnosis, Round 9). Plain text on purpose. -->
  <details class="save-health" ontoggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open) refreshSaveHealth(); }}>
    <summary>Save health · build {BUILD_INFO.commitSha.slice(0, 7)} · {buildSourceLabel(location.href, $nativeWebBuild)}</summary>
    <!-- The send button sits FIRST, directly under the summary: the owner found this
         block by its summary line but never found the copy button buried under the
         log. An action he cannot find is an action that does not exist. -->
    <button
      type="button"
      class="sh-send"
      class:sh-send-sent={sendState === "sent"}
      class:sh-send-failed={sendState === "failed"}
      onclick={sendReport}
      disabled={sendState === "sending"}
      aria-live="polite"
    >
      {sendReportLabel(sendState)}
    </button>
    <p class="sh-line">Waiting to retry: {saveHealth.labelEntries} stroke fix(es), {saveHealth.goalEntries} goal edit(s)</p>
    {#if saveHealth.log.length === 0}
      <p class="sh-line">No save attempts recorded this session.</p>
    {:else}
      {#each saveHealth.log as o (o.ts)}
        <p class="sh-line">{o.ok ? "✓" : "✗"} {o.reason} · {new Date(o.ts).toLocaleTimeString()}{o.workoutUUID ? " · " + o.workoutUUID.slice(0, 8) : ""}</p>
      {/each}
    {/if}
    {#if saveHealth.detail}
      <pre class="sh-detail">{saveHealth.detail}</pre>
    {/if}
    <button type="button" class="sh-copy" onclick={copySaveHealth}>
      {copyState === "copied" ? "Copied ✓" : copyState === "failed" ? "Couldn't copy" : "Copy full report"}
    </button>
  </details>

  {#if $currentFirehoseStatus === "error"}
    <p class="error">Couldn't read this swim from Apple Health yet. Reopen Swim Set Club to try again.</p>
  {:else if $currentFirehoseStatus === "empty"}
    <p class="muted">No swim workout found.</p>
  {:else if $currentFirehoseStatus === "loading" || !dump}
    <p class="muted">
      {$selectedWorkoutUUID
        ? "Loading the selected swim's firehose…"
        : "Loading firehose…"}
    </p>
  {:else}
    <div class="meta">
      <div class="meta-row"><span class="k">When</span><span class="v">{fmtDate(dump.workout.startDate)}</span></div>
      <div class="meta-row"><span class="k">Duration</span><span class="v">{fmtDuration(dump.workout.durationSeconds)}</span></div>
      <div class="meta-row"><span class="k">Activity</span><span class="v">{dump.workout.activityType}</span></div>
      <div class="meta-row"><span class="k">Distance</span><span class="v">
        {dump.workout.totalDistance
          ? `${fmtNum(dump.workout.totalDistance.value)} ${dump.workout.totalDistance.unit}`
          : "—"}
      </span></div>
    </div>

    <details class="group" bind:open={eventsExpanded}>
      <summary>
        <strong>Events</strong> · {dump.events.length}
        <span class="hint">, laps, pauses, and markers the Watch logged</span>
      </summary>
      {#if dump.events.length === 0}
        <p class="muted small">No top-level events.</p>
      {:else}
        <table>
          <thead><tr><th>Type</th><th>Time</th><th>Duration</th><th>Details</th></tr></thead>
          <tbody>
            {#each dump.events as ev, i (i)}
              <tr>
                <td>
                  <span class="event-type">{ev.type}</span>
                  {#if EVENT_TYPE_BLURBS[ev.type]}<div class="muted xsmall">{EVENT_TYPE_BLURBS[ev.type]}</div>{/if}
                </td>
                <td>{fmtTime(ev.date)}</td>
                <td>{ev.durationSeconds == null ? "—" : `${ev.durationSeconds.toFixed(1)}s`}</td>
                <td>
                  {#each renderMetadata(ev.metadata) as m}
                    <div><span class="muted xsmall">{m.label}:</span> <strong>{m.value}</strong></div>
                  {/each}
                  {#if !ev.metadata || Object.keys(ev.metadata).length === 0}<span class="muted">—</span>{/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </details>

    <details class="group" bind:open={activitiesExpanded}>
      <summary>
        <strong>Activities</strong> · {dump.activities.length}
        <span class="hint"> (sub-activities, if the Watch recorded any)</span>
      </summary>
      {#if dump.activities.length === 0}
        <p class="muted small">No multisport activities recorded.</p>
      {:else}
        {#each dump.activities as a (a.uuid)}
          <div class="activity">
            <div><strong>{a.activityType}</strong> · {fmtTime(a.startDate)} → {a.endDate ? fmtTime(a.endDate) : "(open)"} · {fmtDuration(a.durationSeconds)}</div>
            <div class="small muted">Location: {a.locationType ?? "—"}{a.swimmingLocationType ? ` · ${a.swimmingLocationType}` : ""} · {a.events.length} events</div>
          </div>
        {/each}
      {/if}
    </details>

    {@const visibleBuckets = visibleFirehoseBuckets(dump.buckets)}
    <h3>Health metrics · {visibleBuckets.length}</h3>
    <p class="muted small">
      Each row is one kind of health data, and how many readings Apple recorded.
      The empty ones show what Apple did not record.
    </p>

    {#each visibleBuckets as b (b.typeName)}
      {@const info = typeLabel(b.typeName)}
      {@const unit = info.unitLabel ?? b.unit ?? ""}
      <details class="bucket" open={expanded[b.typeName]}>
        <summary onclick={() => toggleBucket(b.typeName)}>
          {#if b.sampleCount === 0}
            <span class="count count-empty">–</span>
          {:else}
            <!-- G8: label the count as READINGS and keep the unit OFF it. The
                 number is a sample count, not a value; "482 bpm" was a misread of
                 the HR reading-count. The per-reading unit shows in the table below. -->
            <span class="count">{b.sampleCount}</span><span class="count-cap">{b.sampleCount === 1 ? "reading" : "readings"}</span>
          {/if}
          <span class="bucket-title">
            <strong>{info.name}</strong>
          </span>
          {#if b.error}<span class="errpill">err</span>{/if}
        </summary>
        <div class="bucket-body">
          <p class="blurb small">{info.blurb}</p>
          {#if b.sampleCount > 0 && unit}<p class="muted xsmall">Measured in {unit}.</p>{/if}
          <p class="muted xsmall raw">Raw type: <code>{b.typeName}</code></p>
          {#if b.error}
            <p class="error small">Error: {b.error}</p>
          {:else if b.samples.length === 0}
            <p class="muted small not-recorded">Apple did not record this.</p>
          {:else}
            <table>
              <thead><tr><th>#</th><th>Start</th><th>End</th><th>Value</th><th>Source</th></tr></thead>
              <tbody>
                {#each b.samples as s, i (i)}
                  <tr>
                    <td>{i + 1}</td>
                    <td>{fmtTime(s.startDate)}</td>
                    <td>{fmtTime(s.endDate)}</td>
                    <td><strong>{fmtNum(s.value ?? null)}</strong>{#if unit}<span class="unit-inline"> {unit}</span>{/if}</td>
                    <td>{s.sourceName ?? "—"}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
        </div>
      </details>
    {/each}
  {/if}
</section>
{/if}

<style>
  .firehose {
    margin-top: 2rem;
    padding: var(--space-4);
    /* Fully opaque (DESIGN.md's literal bg-card #1B1B1D, not the Dawn-Glass
       rgba variant): this section matches app.css's `#app-root section[class]`
       selector (backdrop-filter blur + border) but never carried a --bg-card
       tint at all, so the heading/subtitle/error text sat directly over the
       fixed sunrise photo. Tried the translucent Dawn-Glass value at every
       alpha from 0.42 up to 0.95 and the detector's worst-pixel backdrop-
       filter sample never moved — any translucency leaves that measurement
       mode active. Opaque is what actually clears it, same call as the
       pre-existing showcase-ribbon override for the same reason. The card's
       own 1px border replaces the old top divider. */
    background: #1B1B1D;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    border-radius: var(--radius-card);
  }
  h2 { font-size: 1rem; font-weight: 600; letter-spacing: 0.5px; margin: 0; }
  h3 { font-size: 0.9rem; font-weight: 600; margin: 1.25rem 0 0.25rem; }
  .subtitle { font-size: 0.78rem; color: var(--text-muted); margin: 0.25rem 0 0.75rem; }

  .meta { font-size: 0.85rem; padding: 0.6rem 0.75rem; background: var(--bg-surface); border-radius: 6px; margin-bottom: 0.75rem; }
  /* Save-health readout: quiet diagnostic text, 12px floor respected. */
  .save-health { margin: 0 0 0.75rem; padding: 0.5rem 0.75rem; background: var(--bg-surface); border-radius: var(--radius-button); font-size: 13px; color: var(--text-body); }
  .save-health summary { cursor: pointer; color: var(--text-muted); font-weight: 500; }
  .sh-line { margin: 0.35rem 0 0; font-variant-numeric: tabular-nums; }
  .sh-detail { margin: 0.5rem 0 0; font-size: 12px; color: var(--text-muted); white-space: pre-wrap; word-break: break-all; overflow-x: auto; }
  .sh-copy { margin-top: 0.6rem; padding: var(--space-2) var(--space-4); background: var(--accent-primary); color: var(--text-on-accent); border: 0; border-radius: var(--radius-pill); font-size: 14px; font-weight: 700; cursor: pointer; -webkit-tap-highlight-color: transparent; }
  /* Owner request 2026-07-13: he could not FIND the copy button above — this
     one must be impossible to miss. Full-width, 44px+ tap target, its own
     line, and state is conveyed by the LABEL TEXT (not just color) so it
     reads for a low-vision user regardless of contrast/color perception. */
  .sh-send {
    display: block;
    width: 100%;
    margin: 0.6rem 0 0;
    padding: 0.85rem var(--space-4);
    min-height: 44px;
    background: var(--accent-primary);
    color: var(--text-on-accent);
    border: 0;
    border-radius: var(--radius-button);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .sh-send:disabled { opacity: 0.7; cursor: default; }
  .sh-send-sent { background: var(--accent-primary); }
  .sh-send-failed { background: var(--accent-error); }
  .meta-row { display: flex; justify-content: space-between; padding: 0.15rem 0; gap: 0.5rem; }
  .meta-row .k { color: var(--text-muted); }
  .meta-row .v { text-align: right; }
  .muted { color: var(--text-muted); }
  .small { font-size: 0.78rem; }
  .xsmall { font-size: 0.7rem; }
  .error { color: var(--accent-error); }
  .hint { color: var(--text-dim); font-weight: 400; font-size: 0.75rem; }

  .group { margin: 0.5rem 0; padding: 0.5rem 0.75rem; background: var(--bg-surface); border-radius: 6px; }
  .group summary { cursor: pointer; font-size: 0.85rem; }

  .activity { padding: 0.4rem 0; border-top: 1px solid var(--divider-strong); margin-top: 0.4rem; }
  .event-type { display: inline-block; padding: 0.1rem 0.4rem; background: var(--divider-strong); border-radius: 3px; font-size: 0.75rem; font-weight: 600; }

  .bucket { margin: 0.3rem 0; padding: 0; background: var(--bg-surface); border-radius: 6px; overflow: hidden; }
  .bucket summary { cursor: pointer; padding: 0.5rem 0.6rem; display: flex; align-items: center; gap: 0.6rem; }
  .bucket summary .count { display: inline-block; min-width: 2.5rem; text-align: right; font-variant-numeric: tabular-nums; color: var(--text-muted); font-size: 0.85rem; }
  .bucket summary .count-empty { color: var(--text-dim); font-size: 0.78rem; letter-spacing: 0.05em; }
  .bucket summary .count-cap { color: var(--text-dim); font-size: 0.68rem; margin-left: 0.3rem; }
  .not-recorded { font-style: italic; opacity: 0.6; }
  .bucket summary .bucket-title { flex: 1; font-size: 0.88rem; }
  .bucket summary .errpill { color: var(--accent-error); font-size: 0.68rem; border: 1px solid var(--accent-error); padding: 0 0.3rem; border-radius: 3px; }
  .bucket-body { padding: 0.4rem 0.75rem 0.6rem; }
  .blurb { color: var(--text-body); margin: 0.2rem 0; }
  .raw { margin: 0.1rem 0 0.5rem; }
  .raw code { font-size: 0.7rem; color: var(--text-muted); }
  .unit-inline { color: var(--text-dim); font-size: 0.75rem; font-weight: 400; margin-left: 0.2rem; }

  table { width: 100%; border-collapse: collapse; font-size: 0.75rem; margin-top: 0.4rem; }
  th, td { padding: 0.25rem 0.4rem; text-align: left; border-bottom: 1px solid var(--divider-strong); vertical-align: top; }
  th { color: var(--text-muted); font-weight: 500; }
  td { font-variant-numeric: tabular-nums; }
</style>
